/**
 * Hook for fetching current exchange rates from the backend.
 */

import { useQuery } from '@tanstack/react-query';

import { apiGet } from '@/lib/http/client';

export interface ExchangeRatesResponse {
  rates: Record<string, string>; // backend returns Decimal as string
  base: string;
  date: string | null;
}

export interface ExchangeRatesNormalized {
  rates: Record<string, number>; // parsed to number for math
  base: string;
  date: string | null;
}

/**
 * Fetches current exchange rates for the given org.
 *
 * Rates are cached for 5 minutes (they change daily) and kept in memory for
 * 1 hour. Non-numeric values are filtered out. Callers check `ratesUnavailable`
 * (exported by the stages hooks) to gate converted-total rendering — the endpoint
 * can fail legitimately when no ECB data has been loaded yet.
 *
 * @param orgId - Org ID; query is disabled when undefined.
 */
export function useExchangeRates(orgId: number | undefined) {
  return useQuery({
    queryKey: ['exchangeRates', 'current', orgId],
    queryFn: async (): Promise<ExchangeRatesNormalized> => {
      const resp = await apiGet<ExchangeRatesResponse>(
        `/api/orgs/${orgId}/crm-events/exchange-rates/current/`
      );
      const rates: Record<string, number> = {};
      for (const [k, v] of Object.entries(resp.rates ?? {})) {
        const n = parseFloat(v);
        if (Number.isFinite(n)) {
          rates[k] = n;
        }
      }
      return { rates, base: resp.base, date: resp.date };
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes - rates change daily
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 1, // endpoint can fail legitimately (no ECB data yet); one retry for transient errors
    // A FAILED query has no fresh data, so staleTime cannot protect it: by
    // default every remount re-fires it (+retry). Consumers of this hook live
    // inside per-action keyed subtrees on the actions route, so a
    // legitimately-failing endpoint (no ECB data) was re-fetched on every
    // action switch. Rates change daily — one failed attempt per session
    // (until invalidation/reconnect) is plenty.
    retryOnMount: false,
  });
}
